import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { EventType, Person, Source } from "@shared/types";
import { eventTypesApi, peopleApi, sourcesApi } from "@/api/client";

interface ReferenceDataValue {
  people: Person[];
  eventTypes: EventType[];
  sources: Source[];
  loading: boolean;
  refresh: () => Promise<void>;
}

const ReferenceDataContext = createContext<ReferenceDataValue | null>(null);

export function ReferenceDataProvider({ children }: { children: ReactNode }) {
  const [people, setPeople] = useState<Person[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const [peopleRes, typesRes, sourcesRes] = await Promise.all([
      peopleApi.list(true),
      eventTypesApi.list(true),
      sourcesApi.list(),
    ]);
    setPeople(peopleRes.people);
    setEventTypes(typesRes.eventTypes);
    setSources(sourcesRes.sources);
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const value = useMemo(
    () => ({ people, eventTypes, sources, loading, refresh }),
    [people, eventTypes, sources, loading, refresh],
  );

  return <ReferenceDataContext.Provider value={value}>{children}</ReferenceDataContext.Provider>;
}

export function useReferenceData(): ReferenceDataValue {
  const ctx = useContext(ReferenceDataContext);
  if (!ctx) throw new Error("useReferenceData must be used within ReferenceDataProvider");
  return ctx;
}
